import React from "react";

/**
 * Lightweight skeleton primitives — used for loading states across the app.
 * CSS-only `animate-pulse` (Tailwind built-in keyframe). No JS animation
 * loop. Honors `prefers-reduced-motion` automatically (Tailwind's
 * animate-pulse drops to opacity:1 under that media query).
 *
 * v2.71.22 — Added detail / map / dashboard / list-row variants so every
 * primary mobile screen has a skeleton fallback instead of "Loading…" text.
 */
export const Skel = ({ className = "", style }) => (
  <div
    className={`bg-[#F4F4F5] rounded-sm animate-pulse ${className}`}
    style={style}
    aria-hidden="true"
  />
);

/** Property / project card skeleton (matches PropertyCard layout roughly). */
export const PropertyCardSkeleton = () => (
  <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden" data-testid="skeleton-property-card">
    <Skel className="h-44 w-full rounded-none" />
    <div className="p-4 space-y-2.5">
      <Skel className="h-5 w-3/4" />
      <Skel className="h-3 w-1/2" />
      <div className="flex gap-2 pt-1">
        <Skel className="h-5 w-14 rounded-full" />
        <Skel className="h-5 w-14 rounded-full" />
      </div>
      <Skel className="h-7 w-2/5 mt-2" />
    </div>
  </div>
);

/** Grid of PropertyCardSkeleton (use on dashboards). */
export const PropertyGridSkeleton = ({ count = 6 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="skeleton-property-grid">
    {Array.from({ length: count }).map((_, i) => (
      <PropertyCardSkeleton key={i} />
    ))}
  </div>
);

/** Row skeleton for admin tables — col count configurable. */
export const TableRowSkeleton = ({ cols = 5 }) => (
  <tr data-testid="skeleton-table-row">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="p-3">
        <Skel className={`h-4 ${i === 0 ? "w-32" : "w-20"}`} />
      </td>
    ))}
  </tr>
);

/** Stack of N table rows — drop inside <tbody>. */
export const TableBodySkeleton = ({ rows = 6, cols = 5 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => <TableRowSkeleton key={i} cols={cols} />)}
  </>
);

/** Compact "stat card" skeleton for dashboard headers. */
export const StatCardSkeleton = () => (
  <div className="bg-white border border-[#E4E4E7] rounded-sm p-4 space-y-2" data-testid="skeleton-stat-card">
    <Skel className="h-3 w-20" />
    <Skel className="h-7 w-16" />
  </div>
);

/* ===================================================================== */
/*  v2.71.22 — additional variants for full mobile skeleton coverage     */
/* ===================================================================== */

/** Full PropertyDetail page skeleton — gallery + title + price + specs. */
export const PropertyDetailSkeleton = () => (
  <div className="min-h-screen bg-white" data-testid="skeleton-property-detail">
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-4 sm:py-6 lg:py-8">
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5 sm:gap-8 lg:gap-10">
        {/* Left — gallery + tabs */}
        <div className="min-w-0">
          <Skel className="aspect-[4/3] w-full rounded-sm" />
          <div className="mt-3 flex gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skel key={i} className="h-16 w-20 rounded-sm flex-1 sm:flex-none" />
            ))}
          </div>
          <div className="mt-6 space-y-3">
            <Skel className="h-6 w-2/3" />
            <Skel className="h-4 w-1/2" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="space-y-1.5">
                  <Skel className="h-3 w-16" />
                  <Skel className="h-5 w-20" />
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Right — price + actions rail */}
        <div className="space-y-4">
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-4 sm:p-5 space-y-3">
            <Skel className="h-3 w-12" />
            <Skel className="h-9 w-40" />
            <Skel className="h-3 w-24" />
            <div className="pt-3 space-y-2">
              <Skel className="h-11 w-full rounded-sm" />
              <Skel className="h-11 w-full rounded-sm" />
            </div>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-4 sm:p-5 space-y-2.5">
            <Skel className="h-4 w-28" />
            <Skel className="h-3 w-full" />
            <Skel className="h-3 w-4/5" />
            <Skel className="h-3 w-3/4" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

/** Map view skeleton — sidebar list + framed map area. */
export const MapViewSkeleton = () => (
  <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 py-6" data-testid="skeleton-map-view">
    <div className="mb-4 space-y-2">
      <Skel className="h-7 w-48" />
      <Skel className="h-3 w-64" />
    </div>
    <div className="grid lg:grid-cols-[320px_1fr] gap-4">
      {/* Sidebar listing skeleton */}
      <div className="space-y-3 hidden lg:block">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="bg-white border border-[#E4E4E7] rounded-sm p-3 flex gap-3">
            <Skel className="h-16 w-20 rounded-sm flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <Skel className="h-4 w-3/4" />
              <Skel className="h-3 w-1/2" />
              <Skel className="h-3 w-1/3" />
            </div>
          </div>
        ))}
      </div>
      {/* Map frame */}
      <Skel className="h-[60vh] sm:h-[70vh] w-full rounded-sm" />
    </div>
  </div>
);

/** Single horizontal row skeleton — leads, enquiries, callbacks lists. */
export const ListRowSkeleton = () => (
  <div className="bg-white border border-[#E4E4E7] rounded-sm p-3 sm:p-4 flex items-start gap-3" data-testid="skeleton-list-row">
    <Skel className="h-10 w-10 rounded-full flex-shrink-0" />
    <div className="flex-1 min-w-0 space-y-2">
      <Skel className="h-4 w-2/3" />
      <Skel className="h-3 w-1/2" />
      <Skel className="h-3 w-1/3" />
    </div>
    <Skel className="h-7 w-16 rounded-sm flex-shrink-0 hidden sm:block" />
  </div>
);

/** Stack of N ListRowSkeleton — drop in any vertical-list loading state. */
export const ListSkeleton = ({ rows = 5 }) => (
  <div className="space-y-2.5" data-testid="skeleton-list">
    {Array.from({ length: rows }).map((_, i) => <ListRowSkeleton key={i} />)}
  </div>
);

/** Full dashboard skeleton — header + stat row + list. Used as the
 *  generic "page loading" fallback for Admin/Team/Dealer dashboards. */
export const DashboardSkeleton = ({ stats = 4, rows = 5 }) => (
  <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-8 space-y-6" data-testid="skeleton-dashboard">
    <div className="space-y-2">
      <Skel className="h-7 sm:h-8 w-48" />
      <Skel className="h-3 w-64" />
    </div>
    <div className={`grid grid-cols-2 ${stats >= 4 ? "sm:grid-cols-4" : "sm:grid-cols-3"} gap-3`}>
      {Array.from({ length: stats }).map((_, i) => <StatCardSkeleton key={i} />)}
    </div>
    <ListSkeleton rows={rows} />
  </div>
);

export default PropertyGridSkeleton;
